import React, { useContext } from 'react';
import { Field, FieldArray } from 'formik';
import { List, Button, Space, Row, Col, notification } from 'antd';
import { PlusCircleFilled, DeleteOutlined, CopyFilled } from '@ant-design/icons';
import { DatePickerField, TestItemField, TextField } from 'components/Molecules/FormFields/index';
import { ComplianceMappings } from 'mappers/ComplianceDataMapper';
import get from 'lodash/get';
import { useCreate } from 'context/ResourceProvider/hooks/useCreate';
import { removeKey } from 'helpers/index';
import T from 'i18n-react';
import SelectTestData from '../../components/SelectTestData';
import classes from '../SubForm.module.scss';
import { FormGenerateGccCpcDoc } from '../../context/FormGenerateGccCpcDoc';

const GccCpcTestData = ({ resource, exportMarketId }) => {
  const { complianceDataUrl } = useContext(FormGenerateGccCpcDoc);
  const modalRef = React.useRef(null);
  const { mutate: createTestItem, loading: createTestItemLoading } = useCreate({
    resource: 'test_items',
  });

  const handleSelect = (field) => (selectedRows) => {
    const { value = [] } = field;
    const testData = selectedRows.map(ComplianceMappings.mapTestDataToView);

    const itemToAdd = testData.map((row) => {
      const { serviceProviderId } = row;
      return {
        ...row,
        serviceProvider: {
          name: serviceProviderId.data ? serviceProviderId.data.fullname : serviceProviderId.label,
        },
      };
    });

    field.onChange({
      target: {
        name: field.name,
        value: value.concat(itemToAdd),
      },
    });
  };

  const handleCreateTestItem = async (newOption, { onChange, onSelect }, countryId) => {
    const response = await createTestItem({
      name: newOption.label,
      countryListId: countryId,
    });
    const createdOption = get(response, 'data.data');
    if (createdOption) {
      onChange(createdOption.id, {
        label: createdOption.name,
        value: createdOption.id,
        data: createdOption,
      });
      onSelect();
    } else {
      notification.error({
        message: 'Create new type failed',
      });
    }
  };

  const handleAction = (listValue, arrayHelpers, meta = {}) => () => {
    const { action, index } = meta;
    switch (action) {
      case 'remove': {
        arrayHelpers.remove(index);
        break;
      }
      case 'duplicate': {
        const duplicateTestData = listValue[index];
        arrayHelpers.push(removeKey(duplicateTestData, 'id'));
        break;
      }
      default: {
        break;
      }
    }
  };

  return (
    <Field name="testData">
      {({ field }) => {
        return (
          <FieldArray name="testData">
            {(arrayHelper) => {
              return (
                <div className={classes.testData}>
                  {Array.isArray(field.value) && field.value.length > 0 && (
                    <List
                      split={false}
                      itemLayout="vertical"
                      dataSource={field.value}
                      renderItem={(record, index) => {
                        return (
                          <List.Item className={classes.item}>
                            <div className={classes.meta}>
                              <h5 className={classes.metaTitle}>{`#${index + 1}`}</h5>
                              <Space>
                                <Button
                                  type="text"
                                  icon={<CopyFilled />}
                                  onClick={handleAction(field.value, arrayHelper, {
                                    action: 'duplicate',
                                    index,
                                  })}
                                />
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={handleAction(field.value, arrayHelper, {
                                    action: 'remove',
                                    index,
                                  })}
                                />
                              </Space>
                            </div>

                            <div className={classes.content}>
                              <TestItemField
                                label="Test Item & Citation"
                                required
                                name={`testData.[${index}].testItemId`}
                                countryId={exportMarketId}
                                placeholder={T.translate('placeholder.selectOrFill')}
                                creatable
                                loading={createTestItemLoading}
                                onCreate={handleCreateTestItem}
                                onFilterParams={(params) => {
                                  if (params.keywords) {
                                    const { keywords, ...rest } = params;
                                    return {
                                      ...rest,
                                      keyword: keywords,
                                    };
                                  }
                                  return { ...params };
                                }}
                                onChange={({ field: testItemField, form }, _, option) => {
                                  testItemField.onChange({
                                    target: {
                                      name: `testData.[${index}].testItemId`,
                                      value: option,
                                    },
                                  });
                                  form.setFieldValue(
                                    `testData.[${index}].citationId`,
                                    get(option, 'data.citationList')
                                  );
                                }}
                              />
                              <TextField
                                name={`testData.[${index}].serviceProvider.name`}
                                placeholder={T.translate('placeholder.fillHere')}
                                label="Third Party Laboratory Name"
                                required
                              />

                              <Row gutter={[16, 16]}>
                                <Col span={12}>
                                  <TextField
                                    name={`testData.[${index}].reportId`}
                                    placeholder={T.translate('placeholder.fillHere')}
                                    label="Report Id"
                                  />
                                </Col>
                                <Col span={12}>
                                  <DatePickerField
                                    name={`testData.[${index}].expiryDate`}
                                    label="Expiry Date"
                                  />
                                </Col>
                              </Row>
                              <DatePickerField
                                name={`testData.[${index}].testDate`}
                                label="Test Date / Report Date"
                              />
                            </div>
                          </List.Item>
                        );
                      }}
                    />
                  )}
                  <div>
                    <SelectTestData
                      destroyOnClose
                      width={1100}
                      title="Select Test Data"
                      childRef={modalRef}
                      resource={resource.name}
                      metadata={resource.metadata}
                      filters={{
                        countryId: exportMarketId,
                      }}
                      complianceDataUrl={complianceDataUrl}
                      wrapClassName={classes.overlay}
                      defaultSelectedRows={[]}
                      triggerElement={
                        <Button icon={<PlusCircleFilled />} type="link">
                          Add Existing Test Data
                        </Button>
                      }
                      cancelButtonProps={{
                        hidden: true,
                      }}
                      okText="Confirm"
                      onOk={(closeModal) => {
                        handleSelect(field)(modalRef.current.selectedRows);
                        closeModal();
                      }}
                    />
                    <Button
                      icon={<PlusCircleFilled />}
                      type="link"
                      onClick={() => {
                        arrayHelper.push({
                          testItemId: '',
                          citationId: '',
                          serviceProvider: {
                            name: '',
                          },
                          reportId: '',
                          expiryDate: '',
                        });
                      }}>
                      Add New Test Data
                    </Button>
                  </div>
                </div>
              );
            }}
          </FieldArray>
        );
      }}
    </Field>
  );
};

export default GccCpcTestData;
